import React, { useMemo } from "react";
import { useSubscription } from "../contexts/SubscriptionContext";

const SubscriptionStatus = () => {
  const {
    subscription,
    loading,
    error,
    isSubscriptionActive,
    subscriptionEndDate,
    subscriptionStatus,
    packageInfo, // Get package info from context
  } = useSubscription();

  // Check if subscription has expired
  const isExpired = useMemo(() => {
    if (!subscriptionEndDate) return false;
    const endDate = new Date(subscriptionEndDate);
    const today = new Date();
    // Set both dates to midnight for accurate day comparison
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return endDate < today;
  }, [subscriptionEndDate]);

  // Check if subscription is expiring today
  const isExpiringToday = useMemo(() => {
    if (!subscriptionEndDate) return false;
    const endDate = new Date(subscriptionEndDate);
    const today = new Date();
    // Set both dates to midnight for accurate day comparison
    today.setHours(0, 0, 0, 0);
    endDate.setHours(0, 0, 0, 0);
    return endDate.getTime() === today.getTime();
  }, [subscriptionEndDate]);

  if (loading) {
    return (
      <div className="p-4 bg-blue-50 rounded-lg text-blue-700">
        Loading subscription status...
      </div>
    );
  }

  if (error) {
    return (
      <div className="p-4 bg-red-50 text-red-700 rounded-lg">
        Error: {error}
      </div>
    );
  }

  if (!subscription) {
    return (
      <div className="p-4 bg-yellow-50 text-yellow-700 rounded-lg">
        No subscription found. Please subscribe to continue using our services.
      </div>
    );
  }

  // Only show if expired or expiring today - hide when active and not expiring
  if (!isExpired && !isExpiringToday) {
    return null; // Don't show anything when subscription is active
  }

  // Determine the status message and styling based on subscription state
  let statusMessage = "";
  let statusClass = "";
  let statusBadgeClass = "";
  let badgeText = "";

  if (isExpired) {
    statusMessage = "Subscription Expired";
    statusClass = "bg-red-50 text-red-700";
    statusBadgeClass = "bg-red-100 text-red-800";
    badgeText = "Expired";
  } else if (isExpiringToday) {
    statusMessage = "Subscription Expires Today";
    statusClass = "bg-orange-50 text-orange-700";
    statusBadgeClass = "bg-orange-100 text-orange-800";
    badgeText = "Expiring Today";
  }

  // Get package name from nested package object
  const packageName = packageInfo?.name || subscription?.package?.name || "N/A";

  return (
    <div
      className={`p-4 rounded-lg ${statusClass} border-l-4 ${
        isExpired ? "border-red-500" : "border-orange-500"
      }`}
    >
      <div className="flex justify-between items-start">
        <h3 className="font-bold text-lg mb-2">{statusMessage}</h3>
        <span
          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${statusBadgeClass}`}
        >
          {badgeText}
        </span>
      </div>

      <div className="grid grid-cols-2 gap-2 mt-2">
        {/* Package Name */}
        <div className="font-medium">Plan:</div>
        <div className="font-medium">{packageName}</div>

        {/* Expiration Date */}
        {subscriptionEndDate && (
          <>
            <div className="font-medium">Expiration:</div>
            <div className="font-medium">
              {new Date(subscriptionEndDate).toLocaleDateString("en-US", {
                year: "numeric",
                month: "long",
                day: "numeric",
              })}
              {isExpired && (
                <span className="ml-2 text-sm text-red-600">(Expired)</span>
              )}
              {isExpiringToday && (
                <span className="ml-2 text-sm text-orange-600">
                  (Expires Today)
                </span>
              )}
            </div>
          </>
        )}

        {/* Auto Payment */}
        {subscription.autoPayment !== undefined && (
          <>
            <div className="font-medium">Auto-renewal:</div>
            <div className="font-medium">
              {subscription.autoPayment ? "Enabled" : "Disabled"}
            </div>
          </>
        )}
      </div>

      {/* Action Button - Expired */}
      {isExpired && (
        <div className="mt-4">
          <a
            href="/carrier/package"
            className="inline-block px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
          >
            Renew Subscription
          </a>
          <p className="mt-2 text-sm text-red-600">
            Your subscription has expired. Please renew to continue using all
            features.
          </p>
        </div>
      )}

      {/* Action Button - Expiring Today */}
      {isExpiringToday && (
        <div className="mt-4">
          <a
            href="/carrier/package"
            className="inline-block px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500"
          >
            Renew Now
          </a>
          <p className="mt-2 text-sm text-orange-600">
            Your subscription expires today. Renew now to avoid service
            interruption.
          </p>
        </div>
      )}
    </div>
  );
};

export default SubscriptionStatus;
